import { useState, useEffect } from "react";
import { Bell, Mail, Smartphone, Volume2, VolumeX, X, Save, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Notification type configurations
const NOTIFICATION_TYPES = [
  {
    key: "info_received",
    title: "Paciente respondió información",
    description: "Cuando un paciente responde a tu solicitud de información adicional",
    icon: "📩",
    priority: "alta"
  },
  {
    key: "new_consult_pending",
    title: "Nueva consulta pendiente",
    description: "Cuando llega una nueva consulta pagada sin asignar",
    icon: "🆕",
    priority: "media"
  },
  {
    key: "new_consult_assigned",
    title: "Consulta asignada",
    description: "Cuando se te asigna una consulta específica",
    icon: "📋",
    priority: "media"
  },
  {
    key: "sla_warning",
    title: "Consulta próxima a vencer",
    description: "Cuando una consulta está por superar el tiempo de respuesta",
    icon: "⏰",
    priority: "alta"
  },
  {
    key: "consult_reopened",
    title: "Consulta actualizada",
    description: "Cuando un paciente agrega información a su consulta",
    icon: "🔄",
    priority: "baja"
  }
];

const NotificationTypeRow = ({ config, preferences, onToggle }) => {
  const prefs = preferences[config.key] || { email: true, in_app: true };
  
  const getPriorityBadge = (priority) => {
    const colors = {
      alta: "bg-red-100 text-red-700",
      media: "bg-amber-100 text-amber-700",
      baja: "bg-slate-100 text-slate-600"
    };
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full ${colors[priority]}`}>
        {priority}
      </span>
    );
  };

  return (
    <div className="py-4 border-b border-slate-100 last:border-b-0">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1">
          <span className="text-xl">{config.icon}</span>
          <div>
            <div className="flex items-center gap-2">
              <h4 className="font-medium text-slate-800">{config.title}</h4>
              {getPriorityBadge(config.priority)}
            </div>
            <p className="text-sm text-slate-500 mt-0.5">{config.description}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-6">
          {/* In-App Toggle */}
          <div className="flex items-center gap-2">
            <Smartphone className="h-4 w-4 text-slate-400" />
            <Switch
              checked={prefs.in_app}
              onCheckedChange={(checked) => onToggle(config.key, 'in_app', checked)}
              data-testid={`toggle-${config.key}-inapp`}
            />
          </div>
          
          {/* Email Toggle */}
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-slate-400" />
            <Switch
              checked={prefs.email}
              onCheckedChange={(checked) => onToggle(config.key, 'email', checked)}
              data-testid={`toggle-${config.key}-email`}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const NotificationPreferencesModal = ({ isOpen, onClose }) => {
  const [preferences, setPreferences] = useState({});
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch preferences
  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`${API_URL}/api/notifications/preferences`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setPreferences(data);
          setSoundEnabled(data.sound_enabled !== false);
        }
      } catch (error) {
        console.error('Error fetching preferences:', error);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchPreferences();
      setHasChanges(false);
    }
  }, [isOpen]);

  const handleToggle = (typeKey, channel, value) => {
    setPreferences(prev => ({
      ...prev,
      [typeKey]: {
        ...prev[typeKey],
        [channel]: value
      }
    }));
    setHasChanges(true);
  };

  const handleSoundToggle = (value) => {
    setSoundEnabled(value);
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/notifications/preferences`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...Object.fromEntries(
            NOTIFICATION_TYPES.map(t => [t.key, preferences[t.key] || { email: true, in_app: true }])
          ),
          sound_enabled: soundEnabled
        })
      });

      if (response.ok) {
        toast.success("Preferencias guardadas correctamente");
        setHasChanges(false);
        
        // Update sound preference in localStorage for NotificationBell
        localStorage.setItem('notification_sound_enabled', soundEnabled.toString());
        
        onClose();
      } else {
        toast.error("Error al guardar preferencias");
      }
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error("Error al guardar preferencias");
    } finally {
      setSaving(false);
    }
  };

  const handleEnableAll = () => {
    const newPrefs = {};
    NOTIFICATION_TYPES.forEach(t => {
      newPrefs[t.key] = { email: true, in_app: true };
    });
    setPreferences(prev => ({ ...prev, ...newPrefs }));
    setSoundEnabled(true);
    setHasChanges(true);
  };

  const handleDisableEmails = () => {
    const newPrefs = {};
    NOTIFICATION_TYPES.forEach(t => {
      newPrefs[t.key] = { 
        ...preferences[t.key],
        email: false, 
        in_app: preferences[t.key]?.in_app !== false 
      };
    });
    setPreferences(prev => ({ ...prev, ...newPrefs }));
    setHasChanges(true);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col" data-testid="notification-preferences-modal">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Bell className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">Preferencias de Notificaciones</h2>
              <p className="text-sm text-slate-500">Configura cómo quieres recibir notificaciones</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-2">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : (
            <>
              {/* Quick Actions */}
              <div className="flex gap-2 mb-6">
                <Button variant="outline" size="sm" onClick={handleEnableAll}>
                  Activar todas
                </Button>
                <Button variant="outline" size="sm" onClick={handleDisableEmails}>
                  Desactivar emails
                </Button>
              </div>

              {/* Legend */}
              <div className="flex items-center gap-6 mb-4 text-sm text-slate-500">
                <div className="flex items-center gap-2">
                  <Smartphone className="h-4 w-4" />
                  <span>In-App</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  <span>Email</span>
                </div>
              </div>

              {/* Notification Types */}
              <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
                <div className="px-4">
                  {NOTIFICATION_TYPES.map(config => (
                    <NotificationTypeRow
                      key={config.key}
                      config={config}
                      preferences={preferences}
                      onToggle={handleToggle}
                    />
                  ))}
                </div>
              </div>

              {/* Sound Settings */}
              <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {soundEnabled ? (
                      <Volume2 className="h-5 w-5 text-blue-500" />
                    ) : (
                      <VolumeX className="h-5 w-5 text-slate-400" />
                    )}
                    <div>
                      <h4 className="font-medium text-slate-800">Sonido de notificación</h4>
                      <p className="text-sm text-slate-500">Reproduce un sonido cuando llegan nuevas notificaciones</p>
                    </div>
                  </div>
                  <Switch
                    checked={soundEnabled}
                    onCheckedChange={handleSoundToggle}
                    data-testid="toggle-sound"
                  />
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-200 flex items-center justify-between bg-slate-50">
          <p className="text-sm text-slate-500">
            {hasChanges ? "Tienes cambios sin guardar" : "Sin cambios pendientes"}
          </p>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={!hasChanges || saving}
              data-testid="save-preferences-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Guardar
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationPreferencesModal;
